import type { ReactNode } from 'react'
import { PageHeader } from '../../components/ui/misc'
import { useActiveOrg } from '../../lib/api'

/**
 * One settings page (Personal, Organization, Members, Budgets, Billing): its own header and scroll area. They are
 * separate pages reached from the side nav and the account menu, not tabs.
 */
export function SettingsPage({
  title,
  subtitle,
  wide = false,
  children,
}: {
  title: ReactNode
  /** Organization pages can describe themselves with the active organization's name. */
  subtitle?: ReactNode | ((orgName: string) => ReactNode)
  /** Use the full width of the page instead of the narrow settings column. */
  wide?: boolean
  children: ReactNode
}) {
  const { org } = useActiveOrg()
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PageHeader title={title} subtitle={typeof subtitle === 'function' ? subtitle(org?.name ?? 'your organization') : subtitle} />
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 md:px-8">
        <div className={wide ? 'w-full' : 'max-w-3xl'}>{children}</div>
      </div>
    </div>
  )
}

/**
 * One section of a settings page: a heading (with optional controls on its right), the content, and a hairline
 * before the next section. No boxes.
 */
export function Section({
  title,
  description,
  actions,
  children,
  footer,
}: {
  title: string
  description?: string
  actions?: ReactNode
  children?: ReactNode
  footer?: ReactNode
}) {
  return (
    <section className="border-b border-line-subtle py-6 first:pt-0 last:border-0">
      <div className="flex min-h-[28px] items-center justify-between gap-3">
        <h2 className="text-[14px] font-semibold text-ink">{title}</h2>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {description && <p className="mt-0.5 text-[12.5px] text-ink-muted">{description}</p>}
      {children && <div className="mt-4">{children}</div>}
      {footer && <div className="mt-4 flex justify-end gap-2">{footer}</div>}
    </section>
  )
}
